import { Container, Graphics } from "pixi.js";
import { FIELD, type MatchState } from "@golazo/shared";
import { varFreezeProgress } from "../game/replay.js";

/**
 * La linea de fuera de juego del VAR.
 *
 * Es la imagen que decide la jugada, y por eso se dibuja EXACTAMENTE igual que
 * en televisión: una linea del ancho del campo sobre el penultimo defensor,
 * otra sobre el atacante, y la franja de terreno entre las dos pintada. Lo que
 * convence no es el cartel de "fuera de juego": es ver el hueco.
 *
 * Se pinta en coordenadas de campo, encima del cesped y por debajo de las
 * fichas — como la cal, no como un adorno de HUD. Solo existe durante el
 * congelado de la revision (`varFreezeProgress`), que es cuando los jugadores
 * ya estan colocados en el instante del pase: dibujarla antes seria trazar la
 * linea sobre posiciones que no son las que se juzgaron.
 */
export interface VarLineLayer {
  container: Container;
  sync(state: MatchState): void;
}

/** Cuanto tarda la linea en barrer el campo de banda a banda (fraccion). */
const SWEEP_IN = 0.28;

/*
 * Los colores del VAR son FIJOS, no los de las camisetas.
 *
 * Se probo pintarlas con el acento de cada equipo y era ilegible: con dos
 * equipaciones parecidas —o con una a medida en tonos apagados— las dos lineas
 * salian del mismo color y la prueba no probaba nada. En television pasa lo
 * mismo y se resuelve igual: azul para el defensor, rojo para el atacante,
 * pase lo que pase con los uniformes. Ademas garantiza contraste contra
 * cualquier cesped, que las camisetas tampoco garantizaban.
 */
const DEFEND_LINE = 0x4fc3f7;
const ATTACK_LINE = 0xff5252;
/** La linea del balon, cuando es el quien corta. Neutra: no es de nadie. */
const BALL_LINE = 0xfff3c4;

export function createVarLineLayer(): VarLineLayer {
  const container = new Container();
  container.label = "var-line";
  const g = new Graphics();
  container.addChild(g);

  return {
    container,

    sync(state: MatchState): void {
      const review = state.offsideReview;
      const t = varFreezeProgress(state);
      if (!review || t <= 0) {
        if (container.visible) {
          container.visible = false;
          g.clear();
        }
        return;
      }

      container.visible = true;
      g.clear();

      // La linea se traza barriendo desde el centro hacia las bandas: entra
      // como un trazo, no como un cartel que aparece de golpe.
      const sweep = Math.min(1, t / SWEEP_IN);
      const half = (FIELD.HEIGHT / 2) * sweep;
      const midY = FIELD.HEIGHT / 2;
      const y0 = midY - half;
      const y1 = midY + half;


      /*
       * La franja de ventaja: desde la LINEA QUE DECIDIO hasta el atacante.
       *
       * Es lo que hace legible el fallo de un vistazo. Sin ella hay dos rayas y
       * el espectador tiene que medir a ojo cual va delante — que es justo lo
       * que la repeticion venia a resolver.
       */
      const left = Math.min(review.lineX, review.attacker.x);
      const width = Math.abs(review.attacker.x - review.lineX);
      if (width > 0.5) {
        g.rect(left, y0, width, y1 - y0).fill({ color: ATTACK_LINE, alpha: 0.18 * t });
      }

      /*
       * La linea azul SALE DEL JUGADOR, siempre.
       *
       * Es el penultimo rival, y su disco esta encima: se ve de quien sale. Lo
       * que no puede pasar —y pasaba— es que la raya aparezca flotando en mitad
       * del campo, o encima del balon, sin nadie debajo.
       *
       * Cuando ademas el balon es lo que corta (un pase hacia atras: la regla
       * manda la condicion mas restrictiva), se dibuja una TERCERA linea, fina y
       * de puntos, sobre el balon. Asi la prueba enseña las dos condiciones y no
       * hay que elegir cual mentir.
       */
      drawLine(g, review.defender.x, y0, y1, DEFEND_LINE, t);
      drawLine(g, review.attacker.x, y0, y1, ATTACK_LINE, t);
      if (review.lineFrom === "ball") {
        dottedLine(g, review.ball.x, y0, y1, BALL_LINE, t);
        marker(g, review.ball.x, review.ball.y, BALL_LINE, t, 11);
      }

      // Discos sobre los dos jugadores que definen la jugada: sin ellos las
      // lineas flotan y no se sabe de quien sale cada una.
      marker(g, review.defender.x, review.defender.y, DEFEND_LINE, t);
      marker(g, review.attacker.x, review.attacker.y, ATTACK_LINE, t);
    },
  };
}

function drawLine(
  g: Graphics,
  x: number,
  y0: number,
  y1: number,
  color: number,
  alpha: number,
): void {
  // Doble trazo: un halo ancho y translucido debajo y la linea fina encima.
  // Sobre cesped a rayas una linea sola se pierde en las franjas claras.
  g.moveTo(x, y0).lineTo(x, y1).stroke({ width: 9, color, alpha: 0.22 * alpha });
  g.moveTo(x, y0).lineTo(x, y1).stroke({ width: 2.5, color, alpha: 0.95 * alpha });
}

/** Trazo discontinuo, para que la linea del balon no compita con las de los jugadores. */
function dottedLine(
  g: Graphics,
  x: number,
  y0: number,
  y1: number,
  color: number,
  alpha: number,
): void {
  const dash = 16;
  for (let y = y0; y < y1; y += dash * 2) {
    g.moveTo(x, y).lineTo(x, Math.min(y + dash, y1));
  }
  g.stroke({ width: 2, color, alpha: 0.8 * alpha });
}

function marker(
  g: Graphics,
  x: number,
  y: number,
  color: number,
  alpha: number,
  radius = 17,
): void {
  g.circle(x, y, radius).stroke({ width: 2.5, color, alpha: 0.9 * alpha });
  g.circle(x, y, 4).fill({ color, alpha: 0.95 * alpha });
}
